import type {
  JointBinding,
  JointMimic,
  Joint as SchemaJoint,
} from "@/shared/state/visualization/schema/v1/three-d";
// Shared with `resolveDefaultBindings` rather than open-coded here: a second,
// subtly different parser is how two views of one binding come to disagree.
import { readJointBinding } from "@/shared/state/visualization/schema/v1/three-d/jointBinding";

/**
 * The vendor extension `urdf-to-gltf` writes to carry a robot's kinematics.
 *
 * glTF has no standard for articulation — the Khronos robotics extension has
 * been open since 2020 with no draft — so a converter that wants to preserve
 * joint type, axis and limits has to define its own. Declared in
 * `extensionsUsed` and never `extensionsRequired`, so a viewer that ignores it
 * still draws the robot correctly at its zero pose.
 */
const EXTENSION = "ROBOTO_device";

/**
 * The extension format's major version this module understands.
 *
 * Only the major component is checked, per the extension's own guidance: a minor
 * bump adds optional fields or relaxes a constraint and cannot invalidate a
 * reader, while a major bump means refusing to articulate rather than guessing.
 */
const SUPPORTED_MAJOR = 1;

/** A link of the robot: a named frame, and the glTF node that carries it. */
export interface ArticulationLink {
  name: string;
  /** Index into the document's `nodes` array. */
  node: number;
  /**
   * That node's own name, resolved here while the document is still in hand.
   *
   * Everything downstream addresses nodes by name — Babylon's `AssetContainer`
   * does not preserve glTF indices — so resolving later would be impossible.
   * Absent if the node is unnamed, in which case the link cannot be matched to
   * anything and its joints are not drivable.
   */
  nodeName?: string;
}

/** One joint, reduced to what placing and driving it requires. */
export interface ArticulationJoint {
  name: string;
  type: SchemaJoint["type"];
  /** Index into `links` of the link this joint moves. */
  childLink: number;
  /** As authored — a model file need not supply a unit vector. */
  axis?: [number, number, number];
  lower?: number;
  upper?: number;
  /**
   * The coupling to the joint this one's value is derived from, if any.
   *
   * The extension's `mimic.joint` is an **index** into the document's own joint
   * array, resolved to a name here while that array is still intact. A malformed
   * joint is dropped from the result, so an index carried out of this module
   * would silently address the wrong joint — a gripper's finger following the
   * wrong driver, with nothing to show for it but a robot that moves oddly.
   *
   * `multiplier` and `offset` come across too, filled in with URDF's defaults
   * when the document omits them, so every consumer evaluates the same relation
   * without re-deriving what a missing field means.
   */
  mimic?: JointMimic;
  /**
   * The topic and message path the file declares this joint comes from, if any.
   *
   * Added by extension version 1.1. Read verbatim — nothing is resolved against
   * a workspace here, because a document is parsed long before anyone knows
   * which topics are open. See `resolveDefaultBindings` for that half.
   */
  binding?: JointBinding;
}

export interface Articulation {
  links: ArticulationLink[];
  joints: ArticulationJoint[];
}

const JOINT_TYPES: readonly SchemaJoint["type"][] = [
  "revolute",
  "continuous",
  "prismatic",
  "fixed",
  "floating",
  "planar",
];

/**
 * The robot kinematics a glTF document declares, if it declares any.
 *
 * Reads the parsed JSON rather than the Babylon `AssetContainer`, because glTF
 * *extensions* never survive the loader — only `extras` do, and then only on
 * nodes. The document therefore has to be captured while it is being parsed.
 *
 * Returns `undefined` rather than throwing whenever the extension is absent,
 * unreadable, or a major version ahead: the model still renders, it just cannot
 * be articulated. A single malformed joint is dropped for the same reason —
 * losing one joint beats refusing a whole robot.
 *
 * @param document the parsed glTF JSON, as `onParsed` hands it over
 */
export function readArticulation(
  document: Record<string, unknown> | undefined,
): Articulation | undefined {
  const extension = asRecord(asRecord(document?.extensions)?.[EXTENSION]);
  if (!extension) {
    return undefined;
  }
  if (!isSupportedVersion(extension.version)) {
    return undefined;
  }

  const articulation = asRecord(extension.articulation);
  if (!articulation) {
    return undefined;
  }

  const nodes = asArray(document?.nodes);
  const links = asArray(articulation.links).flatMap((entry) => {
    const link = asRecord(entry);
    const name = link?.name;
    const node = link?.node;
    if (typeof name !== "string" || typeof node !== "number") {
      return [];
    }
    const nodeName = asRecord(nodes[node])?.name;
    return [
      {
        name,
        node,
        ...(typeof nodeName === "string" ? { nodeName } : {}),
      },
    ];
  });

  // Names in document order, taken before any malformed joint is dropped, so a
  // `mimic.joint` index still addresses what the document meant by it.
  const jointNames = asArray(articulation.joints).map((entry) => {
    const name = asRecord(entry)?.name;
    return typeof name === "string" ? name : undefined;
  });
  const joints = asArray(articulation.joints).flatMap((entry) =>
    readJoint(asRecord(entry), jointNames),
  );

  return { links, joints };
}

/** One joint, or nothing at all if it cannot be placed. */
function readJoint(
  joint: Record<string, unknown> | undefined,
  jointNames: (string | undefined)[],
): ArticulationJoint[] {
  const name = joint?.name;
  const type = joint?.type;
  const childLink = joint?.childLink;
  if (
    typeof name !== "string" ||
    typeof childLink !== "number" ||
    typeof type !== "string" ||
    !isJointType(type)
  ) {
    return [];
  }

  const limit = asRecord(joint?.limit);
  const mimic = readMimic(joint?.mimic, jointNames);
  const binding = readJointBinding(joint?.binding);
  return [
    {
      name,
      type,
      childLink,
      ...(readAxis(joint?.axis) ? { axis: readAxis(joint?.axis) } : {}),
      ...(typeof limit?.lower === "number" ? { lower: limit.lower } : {}),
      ...(typeof limit?.upper === "number" ? { upper: limit.upper } : {}),
      ...(mimic !== undefined ? { mimic } : {}),
      ...(binding !== undefined ? { binding } : {}),
    },
  ];
}

/**
 * The coupling relation, with its joint index resolved to a name.
 *
 * `jointNames` is indexed in document order and **before** any malformed joint
 * is dropped, which is the whole reason it is threaded down here: `readJoints`
 * is a `flatMap`, so resolving after the filter would read a shifted array.
 *
 * A reference that resolves to nothing — an index past the end, or a joint the
 * document left unnamed — yields no relation at all rather than a relation to an
 * empty name, so the joint reads as independently drivable instead of as coupled
 * to something that does not exist.
 *
 * `multiplier` and `offset` default to URDF's own 1 and 0.
 */
function readMimic(
  value: unknown,
  jointNames: (string | undefined)[],
): JointMimic | undefined {
  const mimic = asRecord(value);
  if (!mimic || typeof mimic.joint !== "number") {
    return undefined;
  }
  const driver = jointNames[mimic.joint];
  if (driver === undefined) {
    return undefined;
  }
  return {
    joint: driver,
    multiplier: typeof mimic.multiplier === "number" ? mimic.multiplier : 1,
    offset: typeof mimic.offset === "number" ? mimic.offset : 0,
  };
}

function readAxis(value: unknown): [number, number, number] | undefined {
  if (!Array.isArray(value) || value.length !== 3) {
    return undefined;
  }
  const [x, y, z] = value as unknown[];
  if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") {
    return undefined;
  }
  return [x, y, z];
}

function isJointType(value: string): value is SchemaJoint["type"] {
  return (JOINT_TYPES as readonly string[]).includes(value);
}

/** Whether this reader understands the declared format version. */
function isSupportedVersion(value: unknown): boolean {
  if (typeof value !== "string") {
    return false;
  }
  const major = Number.parseInt(value.split(".")[0] ?? "", 10);
  return major === SUPPORTED_MAJOR;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}
